/**
 * Residence document slots — shared shape helpers.
 *
 * Each slot on `residence.documentations` holds an ARRAY of stored file paths,
 * so one document type can carry several files (both sides of a licence, a
 * multi-page contract, and so on). While the manager is editing, that same
 * array also holds freshly picked `File` objects that haven't been uploaded
 * yet — the service splits the two apart when it builds the request.
 *
 * Mirrors RESIDENCE_DOC_FIELDS / MAX_FILES_PER_DOCUMENT in
 * api/middleware/carehome_manager/upload.middleware.js.
 */

export const DOCUMENT_FILE_FIELDS = [
  'photo_id_url',
  'proof_of_address_url',
  'nhs_exemption_certificate_url',
  'additional_id_document_url',
  'signed_admission_contract_url',
  'pre_admission_assessment_url',
  'dnar_form_url',
  'poa_certificate_url',
];

export const MAX_FILES_PER_DOCUMENT = 10;

/**
 * Normalise whatever a slot currently holds into an array.
 *
 * Accepts the current array form, a JSON-encoded array, a single legacy path
 * string, a `File` picked in this session, or null. Empty entries are dropped.
 *
 * @param {*} value
 * @returns {Array<string|File>}
 */
export const toDocumentList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value instanceof File) return [value];
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[')) {
      try {
        return toDocumentList(JSON.parse(text));
      } catch {
        return [];
      }
    }
    return [text];
  }
  return [];
};

/**
 * Label for one entry in a slot: a picked File's own name, or the filename at
 * the end of a stored path.
 *
 * @param {string|File} item
 * @returns {string}
 */
export const documentLabel = (item) => {
  if (item instanceof File) return item.name;
  if (typeof item === 'string') return item.split('/').pop() || item;
  return '';
};
